import React, { useEffect, useState } from 'react'
import { useAppStore } from '../store/useAppStore'
import { useLogStore } from '../store/useLogStore'
import { Car, Package, RefreshCw, LogOut, ChevronRight, Sparkles, Server } from 'lucide-react'
import SyncBadge from '../components/ui/SyncBadge'

export default function HomeScreen() {
  const setStep = useAppStore(s => s.setStep)
  const logout = useAppStore(s => s.logout)
  const credentials = useAppStore(s => s.credentials)
  const contracts = useAppStore(s => s.contracts)
  const logs = useLogStore(s => s.logs)

  const pendingIssues = logs.filter((l: { type: string }) => l.type === 'error').length

  // Salutation dynamique
  const [greeting, setGreeting] = useState('')
  useEffect(() => {
    const hour = new Date().getHours()
    if (hour < 12) setGreeting('Bonjour')
    else if (hour < 18) setGreeting('Bon après-midi')
    else setGreeting('Bonsoir')
  }, [])

  const agentName = (credentials as any)?.username || 'Agent'
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-950 flex flex-col pt-8 pb-12 px-4">
      
      {/* ── Header / Top Bar ── */}
      <div className="flex items-center justify-between mb-8 max-w-4xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 shadow-lg flex items-center justify-center p-2 transform rotate-3">
            <Sparkles className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight">
              {greeting}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">{agentName}</span>
            </h1>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-0.5">
              Mission Control OLYMPIC
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SyncBadge />
          <button 
            onClick={logout}
            className="w-10 h-10 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 text-gray-500 hover:text-red-500 hover:bg-red-50 flex items-center justify-center shadow-sm transition-all"
            title="Déconnexion"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* ── Main Dashboard Actions ── */}
      <div className="flex-1 flex items-center justify-center">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl w-full">
          
          {/* Action 1 : Contrats & Inspections */}
          <button 
            onClick={() => setStep('contracts')}
            className="group relative rounded-[2rem] overflow-hidden text-left bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 active:scale-95"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all" />
            
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg flex items-center justify-center mb-6 relative z-10">
              <Car className="text-white" size={32} strokeWidth={1.5} />
            </div>
            
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-2 relative z-10">
              Contrats
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 relative z-10 font-medium">
              Départs & Retours clients.
            </p>
            
            <div className="flex flex-col gap-2 relative z-10 border-t border-gray-200/50 dark:border-gray-700/50 pt-4">
               <div className="flex items-center justify-between text-xs font-bold text-gray-500 dark:text-gray-400">
                 <span>CONTRATS EN CACHE</span>
                 <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">{contracts?.length || 0}</span>
               </div>
            </div>
          </button>

          {/* Action 2 : États Préparatoires */}
          <button 
            onClick={() => setStep('prep-link')}
            className="group relative rounded-[2rem] overflow-hidden text-left bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 active:scale-95"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-fuchsia-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl group-hover:bg-purple-500/20 transition-all" />
            
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-lg flex items-center justify-center mb-6 relative z-10">
              <Package className="text-white" size={32} strokeWidth={1.5} />
            </div>
            
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-2 relative z-10">
              Préparations
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 relative z-10 font-medium">
              États préparatoires et liaison.
            </p>

            <div className="flex flex-col gap-2 relative z-10 border-t border-gray-200/50 dark:border-gray-700/50 pt-4 mt-auto">
               <div className="flex items-center gap-1.5 text-xs font-bold text-purple-600 dark:text-purple-400">
                 <span>Accéder au hub</span>
                 <ChevronRight size={14} />
               </div>
            </div>
          </button>

          {/* Action 3 : Synchronisation */}
          <button 
            onClick={() => setStep('sync')}
            className="group relative rounded-[2rem] overflow-hidden text-left bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 active:scale-95"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
            
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg flex items-center justify-center mb-6 relative z-10">
              <Server className="text-white" size={32} strokeWidth={1.5} />
            </div>
            
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-2 relative z-10">
              Synchronisation
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 relative z-10 font-medium">
              Mise à jour réseau MyRentCar.
            </p>

            <div className="flex flex-col gap-2 relative z-10 border-t border-gray-200/50 dark:border-gray-700/50 pt-4 mt-auto">
               <div className="flex items-center justify-between text-xs font-bold">
                 <span className="text-gray-500 dark:text-gray-400">ÉTAT RÉSEAU</span>
                 {pendingIssues > 0 ? (
                   <span className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                     <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                     Erreurs
                   </span>
                 ) : (
                   <span className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                     <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                     En ligne
                   </span>
                 )}
               </div>
            </div>
          </button>

        </div>
      </div>
      
    </div>
  )
}
